import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { 
  PackageIcon, 
  ArrowLeft02Icon, 
  Search01Icon,
  HelpCircleIcon,
  ShieldEnergyIcon
} from "hugeicons-react";
import SectionBadge from "../ui/SectionBadge";
import { useNavigate } from "react-router-dom";

export default function OrderTrackingSection() {
  const [trackCode, setTrackCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const tokenPattern = useMemo(() => /^[A-Z0-9]{8}$/, []);

  const sanitizeToken = (token: string) =>
    token.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

  const handleTrack = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const sanitized = sanitizeToken(trackCode);
    if (!sanitized) {
      setError("يرجى إدخال رمز التتبع");
      return;
    }

    if (!tokenPattern.test(sanitized)) {
      setError("تنسيق رمز التتبع غير صحيح. استخدم 8 أحرف/أرقام فقط");
      return;
    }

    navigate(`/track/${sanitized}`);
  };

  return (
    <section className="py-24 relative overflow-hidden bg-[#050000]">
      {/* Background Ambience */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
          
          {/* Content Side */}
          <div className="text-right order-2 lg:order-1" dir="rtl">
            <SectionBadge text="تتبع المسار" icon={PackageIcon} />
            
            <h2 className="text-3xl md:text-5xl font-black text-white mt-6 mb-8 tracking-tight">
              تابع رحلة <span className="text-primary italic">سيارتك</span> لحظة بلحظة
            </h2>
            
            <p className="text-gray-400 text-lg mb-10 leading-relaxed font-bold">
              لا داعي للانتظار أو السؤال. أدخل رمز التتبع الخاص بك لمعرفة حالة الخدمة، تقدم العمل، والوقت المتوقع للتسليم. شفافية كاملة في كل خطوة.
            </p>

            <div className="space-y-6 mb-12">
              {[
                { title: "بث مباشر للحالة", desc: "تعرف إذا كانت سيارتك في مرحلة التنظيف أو التلميع" },
                { title: "إشعارات فورية", desc: "نخبرك بمجرد أن تصبح سيارتك جاهزة للاستلام" },
                { title: "تقارير الجودة", desc: "شاهد صور العمل المنجز مباشرة من مركزنا" },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-1">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-black text-base">{item.title}</h4>
                    <p className="text-gray-500 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Link/Help */}
            <div className="flex items-center gap-3 text-white/40 text-sm font-bold border-t border-white/5 pt-8">
              <ShieldEnergyIcon size={16} />
              <span>نظام تتبع آمن ومشفر بالكامل</span>
            </div>
          </div>

          {/* Form Side */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative order-1 lg:order-2"
          >
            {/* Visual Box */}
            <div className="relative bg-[#0c0c0c] rounded-[40px] p-8 md:p-12 border border-white/10 shadow-[0_0_80px_rgba(0,0,0,0.8)] overflow-hidden group">
              {/* Animated Glow */}
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary/10 blur-[80px] rounded-full transition-opacity group-hover:opacity-20" />
              
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center text-primary mb-8 border border-white/10 shadow-inner">
                  <Search01Icon size={32} />
                </div>
                
                <h3 className="text-2xl font-black text-white mb-2 text-right">أدخل رمز التتبع</h3>
                <p className="text-gray-500 text-sm mb-8 text-right">ستجد الرمز في الرسالة النصية التي وصلتك عند الحجز</p>

                <form onSubmit={handleTrack} className="space-y-4">
                  <div className="relative">
                    <input 
                      type="text"
                      dir="ltr"
                      value={trackCode}
                      onChange={(e) => {
                        setTrackCode(e.target.value);
                        if (error) setError(null);
                      }}
                      placeholder="MF-XXXX-XXXX"
                      className="w-full h-16 bg-black/50 border border-white/10 rounded-2xl px-6 text-center text-xl font-black text-white tracking-widest placeholder:text-white/10 focus:border-primary outline-none transition-all focus:ring-4 focus:ring-primary/10 uppercase"
                    />
                  </div>
                  
                  <button 
                    type="submit"
                    className="w-full h-16 bg-white text-black font-black text-lg rounded-2xl flex items-center justify-center gap-3 hover:bg-primary hover:text-white transition-all transform active:scale-95"
                  >
                    <span>البحث عن الطلب</span>
                    <ArrowLeft02Icon size={20} />
                  </button>

                  {error && (
                    <div className="text-center text-red-400 text-sm font-bold bg-red-500/10 border border-red-500/20 rounded-2xl py-3">
                      {error}
                    </div>
                  )}
                </form>

                <button className="mt-8 flex items-center gap-2 text-white/40 text-xs font-bold hover:text-primary transition-colors mx-auto">
                   <HelpCircleIcon size={14} />
                   <span>فقدت رمز التتبع الخاص بك؟</span>
                </button>
              </div>
            </div>

            {/* Glass decoration */}
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary/20 blur-[60px] rounded-full pointer-events-none" />
          </motion.div>

        </div>
      </div>
    </section>
  );
}
